<template>
    <div class="page-sidebar overflow-y-auto max-w-[25rem] bg-zinc-950 shadow-zinc-500 shadow-xl text-white p-8 flex flex-col justify-between gap-4 h-screen">
        <SectionHue />
        <SectionChroma />
        <SectionLightness />
        <ItemToggle
            label="Show Labels"
            v-model="selectShowLabels"
        />
        <div class="actions flex flex-col gap-2">
            <ItemButton 
                label="Export for Figma" 
                variant="primary" 
                @click="exportToJSONFigma"
            />
            <ItemButton 
                label="Export for WordPress" 
                variant="secondary" 
                @click="exportToJsonWordpress"
            />
        </div>
    </div>
</template>

<script>
import SectionChroma from './SectionChroma.vue'
import SectionHue from './SectionHue.vue'
import SectionLightness from './SectionLightness.vue'
import ItemToggle from './ItemToggle.vue'
import ItemButton from './ItemButton.vue'
import Color from 'colorjs.io'
import { computed } from 'vue'
import { store } from '../store.js'

export default {
    name: 'PageSidebar',
    components: {
        SectionHue,
        SectionChroma,
        SectionLightness,
        ItemToggle,
        ItemButton
    },
    setup() {
        const selectShowLabels = computed({
            get: () => store.showLabels,
            set: (value) => store.setShowLabels(value)
        })

        const exportToJSONFigma = () => {
            const palette = generatePaletteData()
            if (palette) {
                downloadJSON(palette, generateFileName('figma'))
            }
        }

        const exportToJsonWordpress = () => {
            const wordpressPalette = generateWordPressPaletteData()
            if (wordpressPalette) {
                downloadJSON(wordpressPalette, generateFileName('wordpress'))
            }
        }

        const generatePaletteData = () => {
            const currentPalette = store.getCurrentPaletteForExport()
            
            if (!store.baseColor || currentPalette.length === 0) {
                alert('No color palette to export. Please enter a base color first.')
                return null
            }

            // Generate a unique collection ID
            const collectionId = `VariableCollectionId:${Date.now()}:1`
            const modeId = `${Date.now()}:0`
            
            let variableIdCounter = 1
            const variables = []
            const variableIds = []

            // Generate color data for each column (hue) using current palette data
            currentPalette.forEach((columnData) => {
                const colorFamilyName = columnData.profile.name
                
                // Add each lightness step as a Figma variable
                columnData.colors.forEach(colorData => {
                    const variableId = `VariableID:${Date.now()}:${variableIdCounter}`
                    variableIds.push(variableId)
                    
                    // Convert hex to RGB values (0-1 range)
                    const hexColor = colorData.hex
                    const r = parseInt(hexColor.slice(1, 3), 16) / 255
                    const g = parseInt(hexColor.slice(3, 5), 16) / 255
                    const b = parseInt(hexColor.slice(5, 7), 16) / 255

                    const variable = {
                        id: variableId,
                        name: `Colors/${colorFamilyName}-${colorData.step}`,
                        description: `${colorFamilyName}-${colorData.step}${colorData.isBase ? ' (base)' : ''}`,
                        type: "COLOR",
                        valuesByMode: {
                            [modeId]: {
                                r: Math.round(r * 10000) / 10000,
                                g: Math.round(g * 10000) / 10000,
                                b: Math.round(b * 10000) / 10000,
                                a: 1
                            }
                        },
                        resolvedValuesByMode: {
                            [modeId]: {
                                resolvedValue: {
                                    r: Math.round(r * 10000) / 10000,
                                    g: Math.round(g * 10000) / 10000,
                                    b: Math.round(b * 10000) / 10000,
                                    a: 1
                                },
                                alias: null
                            }
                        },
                        scopes: ["ALL_SCOPES"],
                        hiddenFromPublishing: false,
                        codeSyntax: {}
                    }

                    variables.push(variable)
                    variableIdCounter++
                })
            })

            // Create the Figma-compatible structure
            const palette = {
                id: collectionId,
                name: "Color Palette",
                modes: {
                    [modeId]: "Mode 1"
                },
                variableIds: variableIds,
                variables: variables
            }

            return palette
        }

        const generateWordPressPaletteData = () => {
            const currentPalette = store.getCurrentPaletteForExport()
            
            if (!store.baseColor || currentPalette.length === 0) {
                alert('No color palette to export. Please enter a base color first.')
                return null
            }

            const wpPalette = {
                settings: {
                    color: {
                        palette: []
                    }
                },
                metadata: {
                    generatedAt: new Date().toISOString(),
                    baseColor: {
                        hex: new Color('oklch', [store.baseColor.l, store.baseColor.c, store.baseColor.h]).to('srgb').toString({ format: 'hex' }),
                        oklch: {
                            l: Math.round(store.baseColor.l * 1000) / 1000,
                            c: Math.round(store.baseColor.c * 1000) / 1000,
                            h: Math.round(store.baseColor.h * 10) / 10
                        }
                    },
                    settings: {
                        numberOfHues: store.numberOfHues,
                        numberOfStops: store.numberOfStops,
                        splitType: store.splitType,
                        multiplierType: store.multiplierType
                    }
                }
            }

            // Generate WordPress color palette entries using current palette data
            currentPalette.forEach((columnData) => {
                const colorFamilyName = columnData.profile.name

                // Add each lightness step to WordPress palette
                columnData.colors.forEach(colorData => {
                    const capitalizedFamily = colorFamilyName.charAt(0).toUpperCase() + colorFamilyName.slice(1)
                    
                    let name, slug
                    
                    name = `${capitalizedFamily} ${colorData.step}`
                    slug = `${colorFamilyName}-${colorData.step}`

                    wpPalette.settings.color.palette.push({
                        color: colorData.hex,
                        name: name,
                        slug: slug
                    })
                })
            })

            return wpPalette
        }

        const generateFileName = (exportType = 'figma') => {
            const timestamp = new Date().toISOString().slice(0, 19).replace(/:/g, '-')
            const baseColorHex = store.baseColor ? 
                new Color('oklch', [store.baseColor.l, store.baseColor.c, store.baseColor.h])
                    .to('srgb').toString({ format: 'hex' }).replace('#', '') : 'palette'
            
            if (exportType === 'wordpress') {
                return `wp-${baseColorHex}-${timestamp}.json`
            }
            
            return `figma-${baseColorHex}-${timestamp}.json`
        }

        const downloadJSON = (data, filename) => {
            if (!data) return

            const jsonString = JSON.stringify(data, null, 2)
            const blob = new Blob([jsonString], { type: 'application/json' })
            const url = URL.createObjectURL(blob)
            
            const link = document.createElement('a')
            link.href = url
            link.download = filename
            link.style.display = 'none'
            
            document.body.appendChild(link)
            link.click()
            document.body.removeChild(link)
            
            URL.revokeObjectURL(url)
        }

        return {
            selectShowLabels,
            exportToJSONFigma,
            exportToJsonWordpress
        }
    }
}
</script>